import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('webmail_token');
    if (token) {
      authService.validateToken(token)
        .then(userData => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('webmail_token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    // authService stashes token + refresh token + user in localStorage.
    const { user } = await authService.login(email, password);
    setUser(user);
    return user;
  };

  // SSO from CRM — the CRM passes the existing Platform.Auth token along.
  const loginFromCrm = async (token) => {
    const userData = await authService.loginFromCrm(token);
    setUser(userData);
    return userData;
  };

  // Used by SSO callback when we already have a token (no password round-trip).
  const loginWithToken = async (token, preloadedUser = null) => {
    localStorage.setItem('webmail_token', token);
    if (preloadedUser != null) {
      setUser(preloadedUser);
      return preloadedUser;
    }
    const userData = await authService.validateToken(token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginFromCrm,
    loginWithToken,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

